import {
  ChangeEvent,
  Children,
  cloneElement,
  FC,
  Fragment,
  isValidElement,
  MouseEvent,
  PropsWithChildren,
} from 'react';
import { NESTED_COMPONENT, NOT_TRACKED_CONTENT } from '../constants';
import { useNavigate } from 'react-router-dom';
import useTracking from '../hooks/useTracking';

export type EventType = 'click' | 'change';

export interface ITrackedProps {
  contentOverride?: string;
  description?: string;
  events?: EventType[];
  isContentTracked?: boolean;
}

const Tracked: FC<PropsWithChildren<ITrackedProps>> = ({
  children,
  events = ['click'],
  description,
  contentOverride,
  isContentTracked = true,
}) => {
  const navigate = useNavigate();
  const { handleTrack } = useTracking();

  return (
    <Fragment>
      {Children.map(children, (c) => {
        if (!isValidElement(c)) {
          return null;
        }

        const childProps: any = c.props;

        return cloneElement(c as React.ReactElement, {
          // @ts-ignore
          onChange: (e: ChangeEvent<HTMLInputElement>) => {
            childProps.onChange?.(e);

            if (events.includes('change')) {
              handleTrack({
                description,
                checkboxChecked:
                  e.nativeEvent.type !== 'input' ? e.target.checked : undefined,
                eventType: e.nativeEvent.type,
                inputPlaceholder: childProps.placeholder,
                value: isContentTracked
                  ? contentOverride ?? e.target.value
                  : NOT_TRACKED_CONTENT,
              });
            }
          },
          onClick: (e: MouseEvent<Element>) => {
            childProps.onClick?.(e);

            // Wrapper prevents Link working, so this is a workaround
            if (childProps.to) {
              navigate(childProps.to);
            }

            if (events.includes('click')) {
              const content =
                typeof childProps.children === 'string'
                  ? childProps.children
                  : NESTED_COMPONENT;

              handleTrack({
                content: isContentTracked
                  ? contentOverride ?? content
                  : NOT_TRACKED_CONTENT,
                description,
                eventType: e.nativeEvent.type,
                linkTo: childProps.to,
                inputPlaceholder: childProps.placeholder,
              });
            }
          },
        });
      })}
    </Fragment>
  );
};

export default Tracked;
